import React from 'react';
import { Link } from 'react-router-dom';
import { Car, CheckCircle2, Clock, Shield, Wrench, ArrowRight } from 'lucide-react';
import WorkGallery from '../../components/WorkGallery';

const BumperRepair = () => {
  const workImages = [
    '/siteimages/image_0012.jpg',
    '/siteimages/image_0020.jpg',
    '/siteimages/image_0029.jpg',
    '/siteimages/image_0033.jpg',
  ];

  return (
    <div>
      <section className="bg-gradient-to-r from-dark-navy to-dark-teal text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Car className="w-12 h-12 text-bmw-blue" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold">
              Bumper Repair
            </h1>
          </div>
          <p className="text-lg md:text-xl text-soft-grey max-w-3xl">
            Expert bumper repair and restoration services for all types of bumper damage.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-heading font-bold text-dark-navy mb-6">
                Professional <span className="text-bmw-blue">Bumper Restoration</span>
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Bumpers are highly susceptible to damage from parking mishaps, minor collisions, and general wear. 
                Our comprehensive bumper repair service handles cracks, scratches, scuffs, and dents, restoring 
                your bumper to its original condition at a fraction of replacement cost.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We specialize in plastic bumper repairs using advanced welding and filling techniques, followed by 
                precision color-matched painting. The result is a repair that's virtually invisible and as strong as 
                the original bumper.
              </p>

              <div className="bg-bmw-blue bg-opacity-10 border-l-4 border-bmw-blue p-6 rounded-r-lg">
                <h3 className="font-bold text-dark-navy mb-2 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-bmw-blue" />
                  Cost-Effective Solution
                </h3>
                <p className="text-gray-600">
                  Professional bumper repair costs significantly less than replacement while delivering excellent results.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-off-white p-6 rounded-lg">
                <h3 className="text-xl font-bold text-dark-navy mb-4">What We Repair</h3>
                <ul className="space-y-3">
                  {[
                    'Cracks and splits',
                    'Scratches and scuffs',
                    'Parking damage',
                    'Minor collision damage',
                    'Scrapes and gouges',
                    'Paint damage',
                    'Dents and deformation',
                    'Complete bumper respray',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-bmw-blue flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-racing-red bg-opacity-10 border border-racing-red p-6 rounded-lg">
                <h3 className="font-bold text-dark-navy mb-3">Free Bumper Assessment</h3>
                <p className="text-gray-600 mb-4">
                  Get a free assessment and competitive quote for your bumper repair needs.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center gap-2 bg-racing-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
                >
                  Get Free Quote
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-dark-navy text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Quick Turnaround</h3>
              <p className="text-soft-grey">
                Most bumper repairs completed within 2-3 days.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Guaranteed Repairs</h3>
              <p className="text-soft-grey">
                All bumper repairs backed by our comprehensive guarantee.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Car className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Perfect Match</h3>
              <p className="text-soft-grey">
                Color-matched paint for invisible, professional repairs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <WorkGallery images={workImages} title="Bumper Repair Examples" />
        </div>
      </section>

      <section className="py-16 bg-bmw-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Need Bumper Repair?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today for a free assessment and discover how we can restore your bumper to perfect condition.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              Get Free Quote
              <ArrowRight />
            </Link>
            <a 
              href="tel:08000452446" 
              className="bg-white text-bmw-blue px-8 py-4 rounded-lg font-semibold hover:bg-off-white transition shadow-lg"
            >
              Call: 0800 045 2446
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BumperRepair;
